"use client";
import React, { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import ReactPlayer from "react-player";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import FullscreenCarousel from "./EmblaCarousel";

type Post = {
  title?: string;
  images?: string[];
  videoLink?: string;
};

type MediaItem =
  | { type: "image"; src: string }
  | { type: "video"; src: string }
  | { type: "placeholder"; src: string };

const isYouTube = (url: string): boolean => /youtube\.com|youtu\.be/.test(url);
const isBitChute = (url: string): boolean => /bitchute\.com/.test(url);

const placeholderBlur = "/default-placeholder.png";

interface PostMediaCarouselProps {
  post: Post;
}

const PostMediaCarousel: React.FC<PostMediaCarouselProps> = ({ post }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const [isDragging, setIsDragging] = useState(false);

  const mediaItems: MediaItem[] = [];

  if (post.images && post.images.length > 0) {
    post.images.forEach((imgUrl) =>
      mediaItems.push({ type: "image", src: imgUrl })
    );
  }

  if (
    post.videoLink &&
    (isYouTube(post.videoLink) || isBitChute(post.videoLink))
  ) {
    mediaItems.push({ type: "video", src: post.videoLink });
  }

  if (mediaItems.length === 0) {
    mediaItems.push({ type: "placeholder", src: placeholderBlur });
  }

  // Navigation handlers
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect(); // initial check
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;

    const onPointerDown = () => setIsDragging(true);
    const onPointerUp = () => setIsDragging(false);

    emblaApi.on("pointerDown", onPointerDown);
    emblaApi.on("pointerUp", onPointerUp);

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("pointerDown", onPointerDown);
      emblaApi.off("pointerUp", onPointerUp);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);
  const [isFullscreenOpen, setFullscreenOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <>
      {isFullscreenOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
          <div className="relative w-full h-full max-w-5xl mx-auto">
            <FullscreenCarousel
              items={mediaItems}
              initialIndex={activeIndex}
              onClose={() => setFullscreenOpen(false)}
              showNavButtons
            />
            <button
              className="absolute top-4 right-4 text-white text-3xl"
              onClick={() => setFullscreenOpen(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className="relative mx-auto">
        {/* Carousel */}
        <div className="embla overflow-hidden rounded-xl" ref={emblaRef}>
          <div className="embla__container flex">
            {mediaItems.map((item, index) => (
              <div
                onClick={() => {
                  setActiveIndex(index); // current index
                  if (mediaItems.length === 1) return;
                  setFullscreenOpen(true);
                }}
                key={index}
                className="embla__slide flex-shrink-0 w-full"
                style={{
                  position: "relative",
                  minWidth: "100%",
                  height: 500,
                }}
              >
                {item.type === "image" && (
                  <Image
                    src={item.src}
                    alt={post.title ?? "Post media"}
                    layout="fill"
                    objectFit="cover"
                    className="rounded-xl"
                    loading="lazy"
                  />
                )}

                {item.type === "video" && isYouTube(item.src) && (
                  <div
                    className="w-full h-full"
                    style={{ pointerEvents: isDragging ? "none" : "auto" }}
                  >
                    <ReactPlayer
                      url={item.src}
                      width="100%"
                      height="100%"
                      controls
                      style={{ objectFit: "cover", borderRadius: 12 }}
                    />
                  </div>
                )}

                {item.type === "video" && isBitChute(item.src) && (
                  <div
                    className="w-full h-full"
                    style={{ pointerEvents: isDragging ? "none" : "auto" }}
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      src={item.src.replace("/video/", "/embed/")}
                      title={post.title ?? "BitChute video"}
                      allowFullScreen
                      className="rounded-xl"
                    />
                  </div>
                )}

                {item.type === "placeholder" && (
                  <Image
                    src={"/images/not-found-2.webp"}
                    alt="No preview available"
                    layout="fill"
                    objectFit="cover"
                    className="rounded-xl"
                    placeholder="blur"
                    blurDataURL={placeholderBlur}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Navigation buttons */}
        {mediaItems.length > 1 && (
          <>
            <button
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="absolute left-2 top-1/2 z-[100] -translate-y-1/2 bg-white/80 backdrop-blur p-2 rounded-full shadow-md hover:bg-white transition disabled:opacity-30 z-10"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur p-2 rounded-full shadow-md hover:bg-white transition disabled:opacity-30 z-10"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default PostMediaCarousel;
